/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import { ReactNode, useState } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  wrapperClass?: SerializedStyles;
  usePortal?: boolean;
}

const tooltipWrapper = css`
  position: relative;
  display: inline-block;
`;

const tooltipBase = css`
  min-width: 200px;
  max-width: 250px;
  background-color: #3D5360;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  border-radius: 6px;
  padding: 6px 10px;
  word-break: break-word;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: #3D5360 transparent transparent transparent;
  }
`;

const inlineTooltip = css`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.2s ease-in-out;
  z-index: 1;
`;

const inlineVisible = css`
  visibility: visible;
  opacity: 1;
`;

const portalTooltip = css`
  position: fixed;
  z-index: 9999;
  pointer-events: none;
`;

const Tooltip = ({
  content,
  children,
  wrapperClass,
  usePortal = false,
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  return (
    <div
      css={tooltipWrapper}
      onMouseEnter={(e) => {
        if (usePortal) {
          setRect(e.currentTarget.getBoundingClientRect());
        }
        show();
      }}
      onMouseLeave={hide}
    >
      {children}

      {/* Inline tooltip */}
      {!usePortal && content && (
        <div css={[tooltipBase, inlineTooltip, visible && inlineVisible, wrapperClass]}>
          {content}
        </div>
      )}

      {/* Portal tooltip */}
      {usePortal && visible && rect &&
        createPortal(
          <div
            css={[tooltipBase, portalTooltip, wrapperClass]}
            style={{
              top: rect.top - 8,
              left: rect.left + rect.width / 2,
              transform: "translate(-50%, -100%)",
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
